import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { formatAppDate, formatAppDateTime } from '../../utils/dateFormat';
import { Link as RouterLink, Navigate } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';
import { dzenReportApi } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { DzenReportUploadListItem, DzenReportUploadResult } from '../../types';

const DzenReportPage: React.FC = () => {
  const { user } = useAuth();
  const dzenEnabled = !!user?.dzenSubmissionEnabled;
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [recent, setRecent] = useState<DzenReportUploadListItem[]>([]);
  const [lastResult, setLastResult] = useState<DzenReportUploadResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      setRecent(await dzenReportApi.list());
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Ошибка загрузки');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (dzenEnabled) {
      load();
    }
  }, [dzenEnabled]);

  if (!dzenEnabled) {
    return <Navigate to="/cabinet/settings" replace />;
  }

  const onFile = async (file: File | null) => {
    if (!file) return;
    try {
      setUploading(true);
      setError(null);
      setSuccess(null);
      const result = await dzenReportApi.upload(file);
      setLastResult(result);
      setSuccess(
        `Обработано: совпало ${result.matchedCount}, сдано ${result.submittedCount}, без пары ${result.unmatchedCount}`
        + (result.mode === 'MONTHLY' ? ' (ежемесячный режим — числа из файла)' : ' (первичный — дельта)')
      );
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Ошибка загрузки отчёта');
    } finally {
      setUploading(false);
      if (inputRef.current) {
        inputRef.current.value = '';
      }
    }
  };

  return (
    <Box>
      <PageHeader
        title="Дзен — отчёт Студии"
        description="Полуавтомат: загрузите xlsx из Студии → совпадение с креативами → сдача в ОРД."
      />

      {error && <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 1.5 }} onClose={() => setSuccess(null)}>{success}</Alert>}

      <Stack spacing={2}>
        <Paper sx={{ p: 2 }}>
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>Инструкция</Typography>
          <Typography variant="body2" component="div" color="text.secondary">
            Студия → Статистика → Публикации → период → Отчёт ↓.
            <br />
            <strong>Первый раз:</strong> с начала канала до последнего дня прошлого месяца.
            <br />
            <strong>Ежемесячно:</strong> только прошлый месяц (имя файла вида{' '}
            <code>dzen_stat_2026-06-01_2026-06-30.xlsx</code>).
            <br />
            В столбце D — ссылки, E — просмотры. В F можно указать erid вместо «Показы».
            <br />
            Ссылки на креативы: страница{' '}
            <Button component={RouterLink} to="/cabinet/creatives" size="small" sx={{ p: 0, minWidth: 0 }}>
              Креативы
            </Button>
            {' '}→ «Ссылки» → Дзен URL.
          </Typography>
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 2 }}>
            <input
              ref={inputRef}
              type="file"
              accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              hidden
              onChange={(e) => onFile(e.target.files?.[0] || null)}
            />
            <Button
              variant="contained"
              disabled={uploading}
              onClick={() => inputRef.current?.click()}
            >
              {uploading ? <CircularProgress size={20} /> : 'Загрузить отчёт xlsx'}
            </Button>
          </Stack>
        </Paper>

        {lastResult && (
          <Paper sx={{ p: 2 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }} flexWrap="wrap" useFlexGap>
              <Typography variant="subtitle1" fontWeight={700}>Последняя обработка</Typography>
              <Chip size="small" label={lastResult.mode === 'MONTHLY' ? 'ежемесячный' : 'первичный'} />
              {lastResult.periodFrom && lastResult.periodTo && (
                <Chip
                  size="small"
                  variant="outlined"
                  label={`${formatAppDate(lastResult.periodFrom)} — ${formatAppDate(lastResult.periodTo)}`}
                />
              )}
            </Stack>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Статус</TableCell>
                    <TableCell>Креатив</TableCell>
                    <TableCell>ID Дзен</TableCell>
                    <TableCell align="right">В файле</TableCell>
                    <TableCell align="right">Сдано</TableCell>
                    <TableCell>Комментарий</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {lastResult.rows.map((r, i) => (
                    <TableRow key={`${r.dzenVideoId}-${i}`}>
                      <TableCell>
                        <Chip
                          size="small"
                          color={
                            r.status === 'MATCHED_SUBMITTED' ? 'success'
                              : r.status === 'UNMATCHED' || r.status === 'ERROR' ? 'warning'
                                : 'default'
                          }
                          label={r.status}
                        />
                      </TableCell>
                      <TableCell>{r.creativeName || '—'}</TableCell>
                      <TableCell><code>{r.dzenVideoId}</code></TableCell>
                      <TableCell align="right">{r.viewsInFile}</TableCell>
                      <TableCell align="right">{r.showsSubmitted ?? '—'}</TableCell>
                      <TableCell>{r.message || '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}

        <Paper sx={{ p: 2 }}>
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>История загрузок</Typography>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={28} />
            </Box>
          ) : recent.length === 0 ? (
            <Typography color="text.secondary">Пока нет загрузок</Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Когда</TableCell>
                    <TableCell>Файл</TableCell>
                    <TableCell>Режим</TableCell>
                    <TableCell>Период</TableCell>
                    <TableCell align="right">Совпало</TableCell>
                    <TableCell align="right">Сдано</TableCell>
                    <TableCell align="right">Без пары</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {recent.map((u) => (
                    <TableRow key={u.uploadId}>
                      <TableCell>
                        {u.uploadedTs ? formatAppDateTime(u.uploadedTs) : '—'}
                      </TableCell>
                      <TableCell>{u.filename}</TableCell>
                      <TableCell>{u.mode === 'MONTHLY' ? 'ежемес.' : 'первичн.'}</TableCell>
                      <TableCell>
                        {u.periodFrom && u.periodTo
                          ? `${formatAppDate(u.periodFrom)}–${formatAppDate(u.periodTo)}`
                          : '—'}
                      </TableCell>
                      <TableCell align="right">{u.matchedCount}</TableCell>
                      <TableCell align="right">{u.submittedCount}</TableCell>
                      <TableCell align="right">{u.unmatchedCount}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      </Stack>
    </Box>
  );
};

export default DzenReportPage;
